import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Zap, Cpu, Atom, Rocket, Play, Star, Trophy, 
  Lightbulb, Cog, ArrowRight, Sparkles, BookOpen,
  Beaker, Bot, Leaf, Heart, Microscope,
  Power, ChevronRight, ChevronLeft, Globe,
  GraduationCap, Users, ChevronDown, ChevronUp, 
  Layers, FlaskConical, Sun, Waves, Magnet,
  Eye, Menu, X, Handshake
} from 'lucide-react';

const ScienceLabPage = () => {
  const navigate = useNavigate();
  
  const hasActivated = localStorage.getItem('scienceLabActivated') === 'true';
  const [stage, setStage] = useState(hasActivated ? 'main' : 'start');
  const [activationProgress, setActivationProgress] = useState(0);
  const [hoveredModule, setHoveredModule] = useState(null);
  const [glowLines, setGlowLines] = useState([]);
  
  const [viewMode, setViewMode] = useState('quick');
  const [language, setLanguage] = useState('en');
  const [userMode, setUserMode] = useState('student');
  const [expandedClass, setExpandedClass] = useState('class10');
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Quick Access Modules
  const modules = [
    {
      id: 'circuits', title: 'Circuit Lab', titleHindi: 'बिजली का खेल', icon: Zap,
      color: 'from-amber-500 to-orange-500', bgColor: 'bg-amber-50', borderColor: 'border-amber-200',
      description: 'Bulb jalao, circuit banao!', hindiDesc: 'बल्ब जलाओ, सर्किट बनाओ!',
      topics: ['Series Circuit', 'Parallel Circuit', 'Ohm\'s Law'], level: 'Class 6-8', route: '/science/circuits'
    },
    {
      id: 'biology', title: 'Biology Lab', titleHindi: 'जीव विज्ञान', icon: Leaf,
      color: 'from-green-500 to-emerald-500', bgColor: 'bg-green-50', borderColor: 'border-green-200',
      description: 'Human body, cells, plants!', hindiDesc: 'शरीर, कोशिका, पौधे!',
      topics: ['Human Body', 'Plant Life', 'Cell Structure'], level: 'Class 6-10', route: '/science/biology'
    },
    {
      id: 'physics', title: 'Force & Motion', titleHindi: 'बल और गति', icon: Rocket,
      color: 'from-blue-500 to-cyan-500', bgColor: 'bg-blue-50', borderColor: 'border-blue-200',
      description: 'Push, pull, slide!', hindiDesc: 'धक्का दो, खींचो!',
      topics: ['Newton\'s Laws', 'Friction', 'Gravity'], level: 'Class 7-9', route: '/science/physics'
    },
    {
      id: 'machines', title: 'Simple Machines', titleHindi: 'सरल मशीनें', icon: Cog,
      color: 'from-purple-500 to-violet-500', bgColor: 'bg-purple-50', borderColor: 'border-purple-200',
      description: 'Lever, Pulley, Gears!', hindiDesc: 'उत्तोलक, घिरनी, गियर्स!',
      topics: ['Lever', 'Pulley', 'Inclined Plane'], level: 'Class 6-8', route: '/science/machines'
    },
    {
      id: 'robotics', title: 'Robotics', titleHindi: 'रोबोटिक्स', icon: Bot,
      color: 'from-rose-500 to-pink-500', bgColor: 'bg-rose-50', borderColor: 'border-rose-200',
      description: 'Build your own robot!', hindiDesc: 'अपना रोबोट बनाओ!',
      topics: ['Robot Parts', 'Programming', 'Building'], level: 'Class 6-10', route: '/science/robotics', isNew: true
    }
  ];

  // NCERT/CBSE Chapter-wise curriculum
  const curriculum = {
    class10: {
      title: language === 'en' ? 'Class 10 Science' : 'कक्षा 10 विज्ञान',
      chapters: [
        { id: 'ch1-10', number: 1, title: language === 'en' ? 'Chemical Reactions and Equations' : 'रासायनिक अभिक्रियाएँ एवं समीकरण', icon: FlaskConical, color: 'from-orange-500 to-red-500',
          simulations: [
            { id: 'balance-eq', name: language === 'en' ? 'Balance Chemical Equations' : 'रासायनिक समीकरण संतुलित करें', type: 'interactive', ready: true },
            { id: 'reaction-types', name: language === 'en' ? 'Types of Reactions' : 'अभिक्रियाओं के प्रकार', type: 'simulation', ready: true }
          ]
        },
        { id: 'ch2-10', number: 2, title: language === 'en' ? 'Acids, Bases and Salts' : 'अम्ल, क्षारक एवं लवण', icon: Beaker, color: 'from-purple-500 to-indigo-500',
          simulations: [
            { id: 'ph-scale', name: language === 'en' ? 'pH Scale Simulator' : 'pH स्केल सिम्युलेटर', type: 'interactive', ready: true },
            { id: 'litmus-test', name: language === 'en' ? 'Litmus Paper Test' : 'लिटमस पेपर टेस्ट', type: 'simulation', ready: false }
          ]
        },
        { id: 'ch3-10', number: 3, title: language === 'en' ? 'Metals and Non-Metals' : 'धातु एवं अधातु', icon: Layers, color: 'from-gray-500 to-slate-600',
          simulations: [{ id: 'reactivity-series', name: language === 'en' ? 'Reactivity Series' : 'अभिक्रियाशीलता श्रेणी', type: 'interactive', ready: false }]
        },
        { id: 'ch4-10', number: 4, title: language === 'en' ? 'Carbon and its Compounds' : 'कार्बन एवं उसके यौगिक', icon: Atom, color: 'from-gray-700 to-gray-900',
          simulations: [{ id: 'carbon-chains', name: language === 'en' ? 'Carbon Chain Builder' : 'कार्बन श्रृंखला निर्माता', type: 'interactive', ready: false }]
        },
        { id: 'ch5-10', number: 5, title: language === 'en' ? 'Periodic Classification of Elements' : 'तत्वों का आवर्त वर्गीकरण', icon: Layers, color: 'from-teal-500 to-cyan-500',
          simulations: [{ id: 'periodic-table', name: language === 'en' ? 'Interactive Periodic Table' : 'इंटरैक्टिव आवर्त सारणी', type: 'interactive', ready: false }]
        },
        { id: 'ch6-10', number: 6, title: language === 'en' ? 'Life Processes' : 'जैव प्रक्रम', icon: Heart, color: 'from-red-500 to-rose-500',
          simulations: [{ id: 'digestion', name: language === 'en' ? 'Digestive System' : 'पाचन तंत्र', type: 'simulation', ready: true, route: '/science/biology' }]
        },
        { id: 'ch7-10', number: 7, title: language === 'en' ? 'Control and Coordination' : 'नियंत्रण एवं समन्वय', icon: Cpu, color: 'from-violet-500 to-purple-500',
          simulations: [{ id: 'nervous-system', name: language === 'en' ? 'Nervous System Explorer' : 'तंत्रिका तंत्र', type: 'simulation', ready: false }]
        },
        { id: 'ch8-10', number: 8, title: language === 'en' ? 'How do Organisms Reproduce?' : 'जीव जनन कैसे करते हैं?', icon: Leaf, color: 'from-green-500 to-emerald-500',
          simulations: [{ id: 'reproduction', name: language === 'en' ? 'Reproduction Types' : 'प्रजनन के प्रकार', type: 'simulation', ready: false }]
        },
        { id: 'ch9-10', number: 9, title: language === 'en' ? 'Heredity and Evolution' : 'आनुवंशिकता एवं जैव विकास', icon: Microscope, color: 'from-blue-600 to-indigo-600',
          simulations: [{ id: 'genetics', name: language === 'en' ? 'Genetics Simulator' : 'आनुवंशिकी सिम्युलेटर', type: 'interactive', ready: false }]
        },
        { id: 'ch10-10', number: 10, title: language === 'en' ? 'Light - Reflection and Refraction' : 'प्रकाश - परावर्तन तथा अपवर्तन', icon: Sun, color: 'from-yellow-400 to-amber-500',
          simulations: [
            { id: 'ray-diagram', name: language === 'en' ? 'Ray Diagram Simulator' : 'किरण आरेख सिम्युलेटर', type: 'interactive', ready: true },
            { id: 'mirror-types', name: language === 'en' ? 'Mirror Types Explorer' : 'दर्पण प्रकार', type: 'simulation', ready: true },
            { id: 'lens-types', name: language === 'en' ? 'Lens Types Explorer' : 'लेंस प्रकार', type: 'simulation', ready: true }
          ]
        },
        { id: 'ch11-10', number: 11, title: language === 'en' ? 'Human Eye and Colourful World' : 'मानव नेत्र तथा रंगबिरंगा संसार', icon: Eye, color: 'from-cyan-500 to-blue-500',
          simulations: [{ id: 'eye-anatomy', name: language === 'en' ? 'Eye Anatomy' : 'नेत्र शरीर रचना', type: 'simulation', ready: false }]
        },
        { id: 'ch12-10', number: 12, title: language === 'en' ? 'Electricity' : 'विद्युत', icon: Zap, color: 'from-yellow-500 to-orange-500',
          simulations: [
            { id: 'circuit-builder', name: language === 'en' ? 'Circuit Builder' : 'सर्किट बिल्डर', type: 'interactive', ready: true, route: '/science/circuits' },
            { id: 'ohms-law', name: language === 'en' ? 'Ohm\'s Law Calculator' : 'ओम का नियम', type: 'calculator', ready: true }
          ]
        },
        { id: 'ch13-10', number: 13, title: language === 'en' ? 'Magnetic Effects of Electric Current' : 'विद्युत धारा के चुंबकीय प्रभाव', icon: Magnet, color: 'from-red-500 to-rose-600',
          simulations: [{ id: 'electromagnet', name: language === 'en' ? 'Electromagnet Simulator' : 'विद्युत चुंबक', type: 'interactive', ready: false }]
        },
        { id: 'ch14-10', number: 14, title: language === 'en' ? 'Sources of Energy' : 'ऊर्जा के स्रोत', icon: Lightbulb, color: 'from-amber-500 to-yellow-500',
          simulations: [{ id: 'energy-sources', name: language === 'en' ? 'Energy Sources Explorer' : 'ऊर्जा स्रोत', type: 'simulation', ready: false }]
        },
        { id: 'ch15-10', number: 15, title: language === 'en' ? 'Our Environment' : 'हमारा पर्यावरण', icon: Leaf, color: 'from-green-600 to-teal-600',
          simulations: [{ id: 'ecosystem', name: language === 'en' ? 'Ecosystem Simulator' : 'पारिस्थितिकी तंत्र', type: 'simulation', ready: false }]
        },
        { id: 'ch16-10', number: 16, title: language === 'en' ? 'Management of Natural Resources' : 'प्राकृतिक संसाधनों का प्रबंधन', icon: Globe, color: 'from-blue-500 to-green-500',
          simulations: [{ id: 'resources', name: language === 'en' ? 'Resource Management' : 'संसाधन प्रबंधन', type: 'simulation', ready: false }]
        }
      ]
    },
    class9: {
      title: language === 'en' ? 'Class 9 Science' : 'कक्षा 9 विज्ञान',
      chapters: [
        { id: 'ch1-9', number: 1, title: language === 'en' ? 'Matter in Our Surroundings' : 'हमारे आस-पास के पदार्थ', icon: Atom, color: 'from-cyan-500 to-blue-500',
          simulations: [
            { id: 'particle-motion', name: language === 'en' ? '3D Particle Movement' : '3D कण गति', type: 'simulation', ready: true },
            { id: 'states-matter', name: language === 'en' ? 'States of Matter' : 'पदार्थ की अवस्थाएँ', type: 'interactive', ready: true }
          ]
        },
        { id: 'ch2-9', number: 2, title: language === 'en' ? 'Is Matter Around Us Pure?' : 'क्या हमारे आस-पास के पदार्थ शुद्ध हैं?', icon: FlaskConical, color: 'from-green-500 to-emerald-500',
          simulations: [{ id: 'separation-tech', name: language === 'en' ? 'Separation Techniques' : 'पृथक्करण तकनीक', type: 'simulation', ready: false }]
        },
        { id: 'ch3-9', number: 3, title: language === 'en' ? 'Atoms and Molecules' : 'परमाणु एवं अणु', icon: Atom, color: 'from-purple-500 to-violet-500',
          simulations: [{ id: 'atom-builder', name: language === 'en' ? 'Atom Builder' : 'परमाणु निर्माता', type: 'interactive', ready: false }]
        },
        { id: 'ch4-9', number: 4, title: language === 'en' ? 'Structure of the Atom' : 'परमाणु की संरचना', icon: Atom, color: 'from-indigo-500 to-blue-600',
          simulations: [{ id: 'atom-structure', name: language === 'en' ? 'Atomic Structure Explorer' : 'परमाणु संरचना', type: 'interactive', ready: false }]
        },
        { id: 'ch5-9', number: 5, title: language === 'en' ? 'The Fundamental Unit of Life' : 'जीवन की मौलिक इकाई', icon: Microscope, color: 'from-pink-500 to-rose-500',
          simulations: [{ id: 'cell-explorer', name: language === 'en' ? 'Cell Structure Explorer' : 'कोशिका संरचना', type: 'interactive', ready: true, route: '/science/biology' }]
        },
        { id: 'ch6-9', number: 6, title: language === 'en' ? 'Tissues' : 'ऊतक', icon: Layers, color: 'from-red-500 to-rose-500',
          simulations: [{ id: 'tissue-types', name: language === 'en' ? 'Tissue Types' : 'ऊतक प्रकार', type: 'simulation', ready: false }]
        },
        { id: 'ch7-9', number: 7, title: language === 'en' ? 'Diversity in Living Organisms' : 'जीवों में विविधता', icon: Leaf, color: 'from-green-600 to-teal-600',
          simulations: [{ id: 'classification', name: language === 'en' ? 'Classification Explorer' : 'वर्गीकरण', type: 'simulation', ready: false }]
        },
        { id: 'ch8-9', number: 8, title: language === 'en' ? 'Motion' : 'गति', icon: Rocket, color: 'from-blue-500 to-indigo-500',
          simulations: [
            { id: 'motion-graphs', name: language === 'en' ? 'Motion Graphs' : 'गति ग्राफ', type: 'interactive', ready: true, route: '/science/physics' },
            { id: 'velocity-calc', name: language === 'en' ? 'Velocity Calculator' : 'वेग कैलकुलेटर', type: 'calculator', ready: true }
          ]
        },
        { id: 'ch9-9', number: 9, title: language === 'en' ? 'Force and Laws of Motion' : 'बल तथा गति के नियम', icon: Cog, color: 'from-orange-500 to-amber-500',
          simulations: [{ id: 'newtons-laws', name: language === 'en' ? 'Newton\'s Laws Demo' : 'न्यूटन के नियम', type: 'simulation', ready: true, route: '/science/physics' }]
        },
        { id: 'ch10-9', number: 10, title: language === 'en' ? 'Gravitation' : 'गुरुत्वाकर्षण', icon: Globe, color: 'from-purple-500 to-violet-500',
          simulations: [{ id: 'gravity-sim', name: language === 'en' ? 'Gravity Simulator' : 'गुरुत्वाकर्षण सिम्युलेटर', type: 'simulation', ready: false }]
        },
        { id: 'ch11-9', number: 11, title: language === 'en' ? 'Work and Energy' : 'कार्य तथा ऊर्जा', icon: Zap, color: 'from-yellow-500 to-orange-500',
          simulations: [{ id: 'work-energy', name: language === 'en' ? 'Work-Energy Simulator' : 'कार्य-ऊर्जा सिम्युलेटर', type: 'interactive', ready: false }]
        },
        { id: 'ch12-9', number: 12, title: language === 'en' ? 'Sound' : 'ध्वनि', icon: Waves, color: 'from-cyan-500 to-teal-500',
          simulations: [{ id: 'sound-waves', name: language === 'en' ? 'Sound Wave Visualizer' : 'ध्वनि तरंग दृश्य', type: 'simulation', ready: false }]
        },
        { id: 'ch13-9', number: 13, title: language === 'en' ? 'Why Do We Fall Ill?' : 'हम बीमार क्यों होते हैं?', icon: Heart, color: 'from-red-500 to-pink-500',
          simulations: [{ id: 'disease', name: language === 'en' ? 'Disease Explorer' : 'रोग अन्वेषक', type: 'simulation', ready: false }]
        },
        { id: 'ch14-9', number: 14, title: language === 'en' ? 'Natural Resources' : 'प्राकृतिक संपदा', icon: Globe, color: 'from-green-500 to-emerald-500',
          simulations: [{ id: 'natural-resources', name: language === 'en' ? 'Natural Resources Explorer' : 'प्राकृतिक संसाधन', type: 'simulation', ready: false }]
        },
        { id: 'ch15-9', number: 15, title: language === 'en' ? 'Improvement in Food Resources' : 'खाद्य संसाधनों में सुधार', icon: Leaf, color: 'from-lime-500 to-green-500',
          simulations: [{ id: 'food-resources', name: language === 'en' ? 'Food Resources' : 'खाद्य संसाधन', type: 'simulation', ready: false }]
        }
      ]
    }
  };

  const generateGlowLines = () => {
    const lines = [];
    for (let i = 0; i < 12; i++) {
      lines.push({ id: i, angle: i * 30, length: 100 + Math.random() * 200, delay: Math.random() * 0.5, color: ['#0066CC', '#0088DD', '#00AAEE', '#0077BB'][Math.floor(Math.random() * 4)] });
    }
    return lines;
  };

  const handlePowerClick = () => {
    if (stage !== 'start') return;
    setStage('activating');
    setGlowLines(generateGlowLines());
    let progress = 0;
    const interval = setInterval(() => {
      progress += 2;
      setActivationProgress(progress);
      if (progress >= 100) {
        clearInterval(interval);
        localStorage.setItem('scienceLabActivated', 'true');
        setTimeout(() => setStage('main'), 500);
      }
    }, 30);
  };

  const handleSimulationClick = (sim) => {
    if (!sim.ready) {
      alert(language === 'en' ? 'Coming Soon! This simulation is under development.' : 'जल्द आ रहा है! यह सिम्युलेशन विकसित हो रहा है।');
      return;
    }
    if (sim.route) navigate(sim.route);
  };

  // ==================== SCIENCE LAB BADGE ====================
  const PartnershipBadge = () => (
    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-full shadow-sm mb-6">
      <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-md flex items-center justify-center">
        <Atom className="w-4 h-4 text-white" />
      </div>
      <span className="text-sm font-medium text-gray-700">
        {language === 'en' ? 'PractiQ Science Lab' : 'PractiQ विज्ञान प्रयोगशाला'}
      </span>
      <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full font-medium">
        {language === 'en' ? 'CBSE / NCERT' : 'सीबीएसई / एनसीईआरटी'}
      </span>
    </div>
  );

  // ==================== START SCREEN ====================
  const renderStartScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex flex-col items-center justify-center relative overflow-hidden px-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(#0066cc 1px, transparent 1px), linear-gradient(90deg, #0066cc 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Header */}
      <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-20">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
            <Atom className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              PractiQ Science Lab
              <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs border border-blue-200">BETA</span>
            </h1>
            <p className="text-xs text-gray-500">{language === 'en' ? 'Learn Science through Play' : 'खेल-खेल में विज्ञान सीखो'}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setLanguage(language === 'en' ? 'hi' : 'en')}
            className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors shadow-sm"
          >
            <Globe className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-gray-600 font-medium">{language === 'en' ? 'हिन्दी' : 'ENG'}</span>
          </button>
          <Button onClick={() => navigate('/')} variant="outline" className="border-gray-200 text-gray-600 hover:bg-gray-50">
            Home
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center flex flex-col items-center justify-center mt-16">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full mb-8">
          <Sparkles className="w-4 h-4 text-blue-600" />
          <span className="text-sm text-blue-600 font-medium">{language === 'en' ? 'Interactive Science Simulations' : 'इंटरैक्टिव विज्ञान सिम्युलेशन'}</span>
        </div>

        <h2 className="text-4xl md:text-6xl font-bold text-gray-800 mb-2">
          Science <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500">Lab</span>
        </h2>
        <p className="text-gray-500 mb-12">{language === 'en' ? 'Powered by PractiQ Simulator' : 'PractiQ सिम्युलेटर द्वारा संचालित'}</p>

        {/* Power Button */}
        <div className="relative flex items-center justify-center">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-48 h-48 rounded-full border-2 border-blue-300/30 animate-ping" style={{ animationDuration: '2s' }}></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-56 h-56 rounded-full border border-blue-200/50 animate-pulse"></div>
          </div>
          
          <button
            onClick={handlePowerClick}
            className="relative w-40 h-40 rounded-full bg-gradient-to-b from-white to-gray-50 border-4 border-blue-400 flex items-center justify-center shadow-2xl hover:shadow-blue-200 hover:border-blue-500 transition-all duration-300 group cursor-pointer"
            data-testid="science-power-button"
          >
            <Power className="w-16 h-16 text-blue-500 group-hover:text-blue-600 transition-colors" />
            <div className="absolute inset-4 rounded-full bg-gradient-to-b from-blue-50/50 to-transparent"></div>
          </button>
        </div>

        <p className="text-gray-500 mt-12 text-lg animate-pulse">{language === 'en' ? 'Press To Start' : 'शुरू करने के लिए दबाएं'}</p>

        {/* Module Preview */}
        <div className="flex justify-center gap-4 mt-12 flex-wrap max-w-4xl mx-auto">
          {modules.slice(0, 4).map((mod, idx) => (
            <div key={mod.id} className="flex items-center gap-3 px-4 py-3 bg-white border border-gray-200 rounded-xl shadow-sm">
              <span className="text-2xl font-bold text-gray-200">0{idx + 1}</span>
              <div className="text-left">
                <p className="text-sm text-gray-800 font-medium">{mod.title}</p>
                <p className="text-xs text-gray-500">{mod.titleHindi}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ==================== ACTIVATION SCREEN ====================
  const renderActivationScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600 flex flex-col items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center">
        {glowLines.map((line) => (
          <div key={line.id} className="absolute origin-center animate-glow-line"
            style={{ width: `${line.length}px`, height: '3px', background: `linear-gradient(90deg, ${line.color}, transparent)`,
              transform: `rotate(${line.angle}deg)`, animationDelay: `${line.delay}s`, boxShadow: `0 0 20px ${line.color}, 0 0 40px ${line.color}` }}></div>
        ))}
      </div>

      <div className="relative z-10">
        <div className="w-48 h-48 rounded-full bg-gradient-to-br from-white via-blue-100 to-cyan-100 flex items-center justify-center animate-pulse shadow-2xl">
          <div className="w-40 h-40 rounded-full bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center">
            <Atom className="w-20 h-20 text-white animate-spin-slow" />
          </div>
        </div>
        
        <svg className="absolute inset-0 w-48 h-48 -rotate-90">
          <circle cx="96" cy="96" r="90" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
          <circle cx="96" cy="96" r="90" fill="none" stroke="white" strokeWidth="4"
            strokeDasharray={565} strokeDashoffset={565 - (565 * activationProgress) / 100}
            strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.1s ease' }} />
        </svg>
      </div>

      <div className="mt-8 text-center z-10">
        <p className="text-2xl font-bold text-white">{language === 'en' ? 'SYSTEM ACTIVATING' : 'सिस्टम एक्टिवेट हो रहा है'}</p>
        <p className="text-white text-4xl font-mono mt-2">{activationProgress}%</p>
      </div>
    </div>
  );

  // ==================== CHAPTER SIDEBAR ====================
  const renderChapterSidebar = () => (
    <div className={`${mobileMenuOpen ? 'fixed inset-0 z-50 bg-white' : 'hidden lg:block'} lg:relative lg:w-80 lg:min-h-screen lg:border-r lg:border-gray-200 bg-white overflow-y-auto`}>
      <div className="lg:hidden flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <span className="text-gray-800 font-semibold">{language === 'en' ? 'NCERT Chapters' : 'NCERT अध्याय'}</span>
        <button onClick={() => setMobileMenuOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
      </div>

      <div className="p-4">
        {/* Class 10 */}
        <div className="mb-4">
          <button onClick={() => setExpandedClass(expandedClass === 'class10' ? null : 'class10')}
            className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${expandedClass === 'class10' ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-gray-800 font-semibold">{curriculum.class10.title}</p>
                <p className="text-xs text-gray-500">{curriculum.class10.chapters.length} {language === 'en' ? 'Chapters' : 'अध्याय'}</p>
              </div>
            </div>
            {expandedClass === 'class10' ? <ChevronUp className="w-5 h-5 text-blue-500" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
          
          {expandedClass === 'class10' && (
            <div className="mt-2 space-y-1 pl-2 max-h-[40vh] overflow-y-auto">
              {curriculum.class10.chapters.map((chapter) => (
                <button key={chapter.id} onClick={() => { setSelectedChapter(chapter); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left ${selectedChapter?.id === chapter.id ? 'bg-blue-100 border border-blue-300' : 'hover:bg-gray-50 border border-transparent'}`}>
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${chapter.color} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    <chapter.icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-400 font-medium">Ch {chapter.number}</p>
                    <p className="text-sm text-gray-700 truncate">{chapter.title}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Class 9 */}
        <div>
          <button onClick={() => setExpandedClass(expandedClass === 'class9' ? null : 'class9')}
            className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${expandedClass === 'class9' ? 'bg-green-50 border-2 border-green-200' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-md">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-gray-800 font-semibold">{curriculum.class9.title}</p>
                <p className="text-xs text-gray-500">{curriculum.class9.chapters.length} {language === 'en' ? 'Chapters' : 'अध्याय'}</p>
              </div>
            </div>
            {expandedClass === 'class9' ? <ChevronUp className="w-5 h-5 text-green-500" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
          
          {expandedClass === 'class9' && (
            <div className="mt-2 space-y-1 pl-2 max-h-[40vh] overflow-y-auto">
              {curriculum.class9.chapters.map((chapter) => (
                <button key={chapter.id} onClick={() => { setSelectedChapter(chapter); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left ${selectedChapter?.id === chapter.id ? 'bg-green-100 border border-green-300' : 'hover:bg-gray-50 border border-transparent'}`}>
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${chapter.color} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    <chapter.icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-400 font-medium">Ch {chapter.number}</p>
                    <p className="text-sm text-gray-700 truncate">{chapter.title}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ==================== CHAPTER CONTENT (Focus Mode) ====================
  const renderChapterContent = () => {
    if (!selectedChapter) {
      return (
        <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-blue-50 border-2 border-blue-100 rounded-2xl flex items-center justify-center">
              <BookOpen className="w-12 h-12 text-blue-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">{language === 'en' ? 'Select a Chapter' : 'एक अध्याय चुनें'}</h3>
            <p className="text-gray-500 max-w-md">
              {language === 'en' ? 'Choose a chapter from the sidebar to explore interactive simulations aligned with NCERT/CBSE curriculum.' 
                : 'NCERT/CBSE पाठ्यक्रम के अनुसार इंटरैक्टिव सिम्युलेशन देखने के लिए साइडबार से एक अध्याय चुनें।'}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 p-4 lg:p-8 overflow-y-auto bg-gray-50">
        {/* Chapter Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${selectedChapter.color} flex items-center justify-center shadow-lg`}>
              <selectedChapter.icon className="w-8 h-8 text-white" />
            </div>
            <div>
              <p className="text-sm text-gray-400 font-medium">{language === 'en' ? 'Chapter' : 'अध्याय'} {selectedChapter.number}</p>
              <h2 className="text-2xl font-bold text-gray-800">{selectedChapter.title}</h2>
            </div>
          </div>
          
          {userMode === 'teacher' && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-6">
              <p className="text-sm text-blue-700 flex items-center gap-2">
                <Eye className="w-4 h-4" />
                {language === 'en' ? 'Teacher Mode: Labels and explanations are visible for demonstration purposes.' : 'शिक्षक मोड: प्रदर्शन उद्देश्यों के लिए लेबल और स्पष्टीकरण दृश्यमान हैं।'}
              </p>
            </div>
          )}
        </div>

        {/* Simulations Grid (Focus Mode Cards) */}
        <div className="grid md:grid-cols-2 gap-4">
          {selectedChapter.simulations.map((sim) => (
            <Card key={sim.id} onClick={() => handleSimulationClick(sim)}
              className={`relative overflow-hidden cursor-pointer transition-all duration-300 ${sim.ready ? 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-lg hover:scale-[1.02]' : 'bg-gray-50 border-gray-200 opacity-70'}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${selectedChapter.color} flex items-center justify-center shadow-md`}>
                    {sim.type === 'interactive' && <Play className="w-6 h-6 text-white" />}
                    {sim.type === 'simulation' && <Atom className="w-6 h-6 text-white" />}
                    {sim.type === 'calculator' && <Cpu className="w-6 h-6 text-white" />}
                  </div>
                  {sim.ready ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1 border border-green-200">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                      {language === 'en' ? 'Ready' : 'तैयार'}
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full border border-amber-200">
                      {language === 'en' ? 'Coming Soon' : 'जल्द आ रहा है'}
                    </span>
                  )}
                </div>
                
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{sim.name}</h3>
                <p className="text-sm text-gray-500 capitalize">{sim.type}</p>
                
                {sim.ready && (
                  <div className="mt-4 flex items-center gap-2 text-blue-600 text-sm font-medium">
                    <span>{language === 'en' ? 'Launch Simulation' : 'सिम्युलेशन शुरू करें'}</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  // ==================== QUICK ACCESS VIEW ====================
  const renderQuickAccessView = () => (
    <div className="min-h-[calc(100vh-80px)] bg-gray-50 relative overflow-hidden">
      <section className="relative z-10 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            {/* Partnership Badge - Subtle Pill */}
            <PartnershipBadge />
            
            {/* Mascot Icon */}
            <div className="mb-6 relative inline-block">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500 rounded-2xl flex items-center justify-center shadow-xl animate-bounce-slow">
                <Bot className="w-12 h-12 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center animate-pulse shadow-lg">
                <Star className="w-4 h-4 text-white" />
              </div>
            </div>

            <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-4">
              {language === 'en' ? 'Namaste, ' : 'नमस्ते, '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500">
                {language === 'en' ? 'Young Scientists!' : 'युवा वैज्ञानिकों!'}
              </span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'en' ? 'Learn science through interactive games and simulations!' : 'इंटरैक्टिव गेम्स और सिम्युलेशन के माध्यम से विज्ञान सीखें!'}
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              {[
                { icon: Play, text: language === 'en' ? 'Interactive Sims' : 'इंटरैक्टिव सिम' },
                { icon: BookOpen, text: language === 'en' ? 'NCERT Aligned' : 'NCERT अनुरूप' },
                { icon: Trophy, text: language === 'en' ? 'Earn Badges' : 'बैज अर्जित करें' }
              ].map((feat, idx) => (
                <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full shadow-sm">
                  <feat.icon className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-gray-600">{feat.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Module Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 max-w-7xl mx-auto">
            {modules.map((module, idx) => (
              <Card key={module.id}
                className={`relative overflow-hidden ${module.bgColor} ${module.borderColor} border-2 hover:shadow-xl cursor-pointer group transition-all duration-300 hover:scale-105`}
                onMouseEnter={() => setHoveredModule(module.id)} onMouseLeave={() => setHoveredModule(null)} onClick={() => navigate(module.route)}>
                <CardContent className="p-5 relative z-10">
                  {module.isNew && <div className="absolute top-3 right-3"><span className="px-2 py-0.5 bg-rose-500 text-white text-xs font-bold rounded-full animate-pulse shadow-md">NEW</span></div>}
                  <div className="absolute top-4 right-4 text-3xl font-bold text-gray-200">0{idx + 1}</div>
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${module.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <module.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-1">{module.title}</h3>
                  <p className="text-sm text-gray-500 mb-2">{module.titleHindi}</p>
                  <p className="text-sm text-gray-600 mb-4">{language === 'en' ? module.description : module.hindiDesc}</p>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {module.topics.slice(0, 2).map((topic, i) => (
                      <span key={i} className="text-xs px-2 py-1 bg-white/80 rounded-full text-gray-600 border border-gray-200">{topic}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{module.level}</span>
                    <div className="flex items-center gap-1 text-blue-600 group-hover:translate-x-1 transition-transform">
                      <span className="text-xs font-medium">{language === 'en' ? 'Explore' : 'देखें'}</span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </CardContent>
                <div className="absolute top-4 left-4">
                  <span className="flex items-center gap-1 px-2 py-1 bg-green-100 border border-green-200 rounded-full text-xs text-green-700">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>Live
                  </span>
                </div>
              </Card>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-10">
            <Button size="lg" onClick={() => navigate('/science/circuits')}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg group">
              <Zap className="w-5 h-5 mr-2 group-hover:animate-pulse" />
              {language === 'en' ? 'Start Experiments' : 'प्रयोग शुरू करें'}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );

  // ==================== MAIN SCREEN ====================
  const renderMainScreen = () => (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 backdrop-blur-sm">
        <div className="max-w-full mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left */}
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ChevronLeft className="w-5 h-5 text-gray-500" />
              </button>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-md">
                <Atom className="w-6 h-6 text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  PractiQ Science Lab
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs border border-green-200">LIVE</span>
                </h1>
              </div>
            </div>
            
            {/* Center - View Toggle */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1 border border-gray-200">
              <button onClick={() => setViewMode('quick')}
                className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${viewMode === 'quick' ? 'bg-white text-blue-600 shadow-sm border border-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
                {language === 'en' ? 'Quick Lab Access' : 'त्वरित लैब पहुंच'}
              </button>
              <button onClick={() => setViewMode('chapter')}
                className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all flex items-center gap-1 ${viewMode === 'chapter' ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-700'}`}>
                <GraduationCap className="w-4 h-4" />
                <span className="hidden sm:inline">{language === 'en' ? 'CBSE/NCERT Syllabus' : 'CBSE/NCERT पाठ्यक्रम'}</span>
                <span className="sm:hidden">{language === 'en' ? 'Chapters' : 'अध्याय'}</span>
              </button>
            </div>

            {/* Right */}
            <div className="flex items-center gap-2">
              {/* Hindi Toggle - Always Visible */}
              <button onClick={() => setLanguage(language === 'en' ? 'hi' : 'en')}
                className="flex items-center gap-1 px-3 py-2 bg-gray-100 border border-gray-200 rounded-full hover:bg-gray-200 transition-colors"
                title={language === 'en' ? 'Switch to Hindi' : 'Switch to English'}>
                <Globe className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-gray-600 font-medium">{language === 'en' ? 'हिन्दी' : 'ENG'}</span>
              </button>

              <button onClick={() => setUserMode(userMode === 'student' ? 'teacher' : 'student')}
                className={`flex items-center gap-1 px-3 py-2 rounded-full transition-colors ${userMode === 'teacher' ? 'bg-blue-100 border border-blue-200 text-blue-700' : 'bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200'}`}>
                {userMode === 'teacher' ? <Eye className="w-4 h-4" /> : <Users className="w-4 h-4" />}
                <span className="text-xs hidden sm:inline">{userMode === 'teacher' ? (language === 'en' ? 'Teacher' : 'शिक्षक') : (language === 'en' ? 'Student' : 'छात्र')}</span>
              </button>

              {viewMode === 'chapter' && (
                <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden p-2 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200">
                  <Menu className="w-5 h-5 text-gray-500" />
                </button>
              )}

              <Button onClick={() => navigate('/')} variant="outline" size="sm" className="border-gray-200 text-gray-600 hover:bg-gray-50 hidden sm:flex">Home</Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      {viewMode === 'quick' ? renderQuickAccessView() : (
        <div className="flex flex-1">
          {renderChapterSidebar()}
          {renderChapterContent()}
        </div>
      )}

      {/* Footer */}
      <footer className="py-6 border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm">
            PractiQ Science Lab - {language === 'en' ? 'Interactive Learning for Classes 6-10 | NCERT/CBSE Aligned' : 'कक्षा 6-10 के लिए इंटरैक्टिव लर्निंग | NCERT/CBSE अनुरूप'}
          </p>
        </div>
      </footer>
    </div>
  );

  // ==================== RENDER ====================
  return (
    <>
      {stage === 'start' && renderStartScreen()}
      {stage === 'activating' && renderActivationScreen()}
      {stage === 'main' && renderMainScreen()}
      
      <style>{`
        @keyframes glow-line { 0% { opacity: 0; transform: rotate(var(--angle, 0deg)) scaleX(0); } 50% { opacity: 1; } 100% { opacity: 0; transform: rotate(var(--angle, 0deg)) scaleX(0); } }
        @keyframes bounce-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-glow-line { animation: glow-line 1.5s ease-out forwards; }
        .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
        .animate-spin-slow { animation: spin-slow 8s linear infinite; }
      `}</style>
    </>
  );
};

export default ScienceLabPage;
